<template>
  <div class="message-panel-container chat-panel-container">
    <div class="ibox chat-panel">
      <div class="ibox-title">
        <div class="drag-indicator">
          <i class="fa fa-reorder"></i>
        </div>
        <h5>Chat</h5>
        <div class="ibox-tools">
          <el-dropdown @command="handleTopicChange" v-if="totalNotifications > 0">
            <el-button type="text">
              <el-badge :value="totalNotifications" class="new-message">
                <i class="fa fa-bell"></i>
              </el-badge>
              <i class="fa fa-bell" v-if="totalNotifications === 0"></i>
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="notificationItem in notificationItems"
                :key="notificationItem.topic"
                :command="notificationItem.topic"
              >
                <div class="notification-item">
                  <div class="notification-prepend">{{ notificationItem.prepend }}</div>
                  <div class="notification-name">{{ notificationItem.name }}</div>
                  <div class="notification-count">{{ notificationItem.count }}</div>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-tooltip content="Close panel" effect="dark" placement="bottom">
            <el-button type="text" @click="$emit('closePanel')">
              <i class="fa fa-times"></i>
            </el-button>
          </el-tooltip>
        </div>
      </div>
      <div class="ibox-content">
        <channel-selector
          :channels="channelsList"
          :notifications="notifications"
          :selected-topic="topic"
          :presences="presences"
          @onSelectTopic="handleTopicChange"
          class="channel-selector"
        />
        <div class="chat-area">
          <perfect-scrollbar class="chat-content" ref="content" @ps-scroll-y="handleScroll">
            <div class="error" v-if="channelError">Unable to join this converstaion</div>
            <div v-for="message in computedMessages" :key="message.id">
              <div class="message" :class="message.direction">
                <div class="name" v-if="message.direction === 'in'">
                  {{ message.user.first_name }} {{ message.user.last_name }}
                </div>
                <div class="name" v-if="message.direction === 'out'">You</div>
                {{ message.body }}
                <div class="datetime">{{ displayDatetime(message.datetime) }}</div>
              </div>
              <div class="clearfix"></div>
            </div>
            <div ref="bottomScroll"></div>
          </perfect-scrollbar>
          <div class="chat-input">
            <el-input
              v-model="chatInput"
              @keyup.enter.native="submit"
              @focus="resetNotifications"
              @blur="resetNotifications"
            >
              <el-button slot="append" icon="fa fa-arrow-up" @click="submit" />
            </el-input>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import moment from 'moment';
import { Presence } from 'phoenix';
import { PerfectScrollbar } from 'vue2-perfect-scrollbar';

import ChannelSelector from '@/components/chat/ChannelSelector';

export default {
  name: 'Chat',
  components: {
    ChannelSelector,
    PerfectScrollbar,
  },

  data: () => ({
    topic: 'all',
    chatInput: '',
    channelsList: [],
    page: 1,
    messages: [],
    notifications: {},
    waitForUserResolve: false,
    channelError: false,
    rerenderInterval: null,
    presences: {},
    isFetchingMore: false,
    scrollHeightBefore: 0,
  }),

  mounted() {
    // Ensure all auth has completed already
    if (this.me.use_as_company) {
      this.startPresence();
      this.startChannel();
    } else {
      this.waitForUserResolve = true;
    }

    this.rerenderInterval = setInterval(() => {
      this.$forceUpdate();
    }, 60 * 1000);
  },

  destroyed() {
    clearInterval(this.rerenderInterval);
  },

  computed: {
    ...mapState('user', ['me']),

    computedMessages() {
      return this.messages.map(message => ({
        ...message,
        direction: this.me.id === message.user.id ? 'out' : 'in',
      }));
    },

    notificationItems() {
      return Object.keys(this.notifications)
        .filter(key => key !== 'total')
        .map(key => {
          let prepend = '';
          if (key.startsWith('tag:')) {
            prepend = '#';
          } else if (key.startsWith('user:')) {
            prepend = '@';
          }

          return {
            prepend,
            topic: key,
            name: this.notifications[key].name,
            count: this.notifications[key].count,
          };
        });
    },

    totalNotifications() {
      return Object.keys(this.notifications).reduce(
        (acc, key) => acc + this.notifications[key].count,
        0,
      );
    },
  },

  methods: {
    /* eslint-disable no-console */
    startPresence() {
      this.presenceChannel = window.phoenixSocket.channel('chat:presence', {});
      this.presenceChannel
        .join()
        .receive('ok', resp => {
          console.log('Presence joined successfully', resp);
        })
        .receive('error', resp => {
          console.log('Unable to join presence', resp);
        });

      this.presenceChannel.on('presence_state', state => {
        this.presences = Presence.syncState(this.presences, state);
      });

      this.presenceChannel.on('presence_diff', diff => {
        this.presences = Presence.syncDiff(this.presences, diff);
      });
    },

    startChannel() {
      this.channelError = false;

      const topic = `${this.me.use_as_company}.${this.topic}`;
      this.channel = window.phoenixSocket.channel(`room:topic:${topic}`, {});

      this.channel
        .join()
        .receive('ok', resp => {
          console.log('Chat joined successfully', resp);
          this.messages = resp.messages.reverse();
          this.notifications = resp.notifications;
          Vue.nextTick(() => this.scrollToBottom());
        })
        .receive('error', resp => {
          console.log('Unable to join chat', resp);
          this.channelError = true;
          this.channel.leave();
        });

      this.channel.on(`room:topic:${topic}:new_message`, message => {
        this.messages.push(message);
        Vue.nextTick(() => this.scrollToBottom());
      });

      this.channel.on('user:new_notification', notification => {
        const notifyTopic = Object.keys(notification)[0];
        this.notifications = { ...this.notifications, [notifyTopic]: notification[notifyTopic] };
        this.playNotificationSound();
      });

      this.channel.on('user:reset_notification', resetNotification => {
        const { [resetNotification.topic]: remove, ...rest } = this.notifications;
        this.notifications = rest;
      });

      this.channel.on('message:more', response => {
        this.messages = [...response.messages.reverse(), ...this.messages];
        this.isFetchingMore = false;
        Vue.nextTick(this.adjustScrollPosition);
      });
    },

    loadMore() {
      const offset = this.messages.length;
      if (offset > 0) {
        this.channel.push('message:get_more', { offset });
        this.isFetchingMore = true;
      }
    },

    playNotificationSound() {
      // eslint-disable-next-line
      new Audio(require('@/assets/sounds/notification-sound.mp3')).play();
    },

    changeChannel() {
      this.reset();
      this.startChannel();
    },

    reset() {
      this.channelError = false;
      if (this.channel) {
        this.channel.leave();
      }
      this.messages = [];
      this.page = 1;
    },

    submit() {
      this.channel.push('message:add', {
        message: this.chatInput,
      });

      this.chatInput = '';
    },

    resetNotifications() {
      this.channel.push('notification:reset', {});
    },

    displayDatetime(datetime) {
      return moment(datetime).fromNow();
    },

    scrollToBottom() {
      const el = this.$refs.bottomScroll;

      if (el) {
        el.scrollIntoView({ behaviour: 'smooth' });
      }
    },

    adjustScrollPosition() {
      const scrollHeightAfter =
        this.$refs.content.$el.scrollHeight - this.$refs.content.$el.clientHeight;
      this.$refs.content.$el.scrollTop = scrollHeightAfter - this.scrollHeightBefore;
    },

    handleTopicChange(topic) {
      this.topic = topic;
      this.changeChannel();
    },

    handleScroll() {
      this.scrollHeightBefore =
        this.$refs.content.$el.scrollHeight - this.$refs.content.$el.clientHeight;

      const scrollPosition = this.$refs.content.$el.scrollTop;
      if (scrollPosition === 0 && !this.isFetchingMore) {
        this.loadMore();
      }
    },
  },
  watch: {
    me() {
      if (this.waitForUserResolve) {
        this.startChannel();
        this.startPresence();
        this.waitForUserResolve = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/style/_variables_live.scss';
@import '../../assets/style/animate.css';

.chat-panel-container {
  height: calc(100vh - 125px);
  display: inline-block;
  background: transparent;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-right: 15px;
  border: 1px solid #e9e9e9;
  box-shadow: 1px 1px 7px 0 #ddd;
  white-space: normal;
  vertical-align: top;

  &.fixed-container {
    width: 100%;
  }
}

.chat-panel {
  width: 600px;
  height: 95%;
  position: static;

  &.fixed {
    width: 100%;
    margin-top: 0;
  }

  &.column-layout-1 {
    width: 400px;
  }

  &.column-layout-2 {
    width: 800px;
  }

  &.column-layout-3 {
    width: 1200px;
  }

  .ibox-content {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: row;
    overflow: hidden;
  }

  .ibox-title {
    border-top: 2px solid $colony-green;

    h5 {
      font-size: 0.95em;
    }
    .drag-indicator {
      position: relative;
      margin-top: -8px;
      margin-bottom: -8px;
      font-size: 10px;
      color: $colony-light-gray;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .ibox-tools {
      margin-top: -3px;
      cursor: grab;

      button {
        color: inherit;
        margin: -10px 2px;
      }
    }
  }
}

.ibox {
  border: 0;
  box-shadow: 0;
}

.chat-area {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.chat-content {
  padding: 0 8px;
  flex-grow: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .error {
    font-size: 1.1em;
    text-align: center;
    margin-top: 3em;
    color: $colony-red;
  }
}

.message {
  padding: 1rem 1.2rem;
  margin: 1rem 0;
  border-radius: 6px;
  display: inline-block;

  &.in {
    float: left;
    text-align: left;
    background-color: rgb(229, 231, 235);

    .name {
      text-align: right;
    }
  }

  &.out {
    float: right;
    text-align: right;
    background-color: rgb(191, 219, 254);

    .name {
      text-align: left;
    }
  }

  .name {
    text-transform: uppercase;
    font-size: 0.6em;
    font-weight: bold;
    color: rgb(55, 65, 81);
    margin-bottom: 0.6em;
  }
  .datetime {
    color: rgb(55, 65, 81);
    font-size: 0.93rem;
    text-align: right;
  }
}

.notification-item {
  display: flex;
  .notification-prepend {
    font-weight: bold;
    font-size: 1.1em;
    margin-right: 2px;
    color: $colony-dark-gray;
  }

  .notification-name {
    flex-grow: 1;
  }

  .notification-count {
    margin-left: 3em;
    background-color: $colony-red;
    color: $white;
    border-radius: 9999px;
    height: 18px;
    font-size: 12px;
    line-height: 12px;
    text-align: center;
    padding: 0 6px;
    display: inline-block;
  }
}

.chat-input {
  input {
    width: 100%;
  }
}

.channel-selector {
  background: #fafafa;
  height: 100%;
  border-right: 1px solid $colony-light-gray;
  padding-right: 8px;
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// Chat.vue?b59daa2e